import type { ReactNode, Ref } from 'react';
import { LocateFixed, Navigation, Route, SlidersHorizontal } from 'lucide-react';

import type { TrackingMode } from '../types';
import type { ChromeMode } from './theme';

export interface AppShellProps {
  children: ReactNode;
  chromeMode: ChromeMode;
  brandHref: string;
  brandImageSrc: string;
  status: string;
  tracking: TrackingMode;
  journeyText?: string;
  labels?: {
    journey: string;
    location: string;
    tools: string;
  };
  journeyLauncherRef?: Ref<HTMLButtonElement>;
  toolsLauncherRef?: Ref<HTMLButtonElement>;
  onJourney: () => void;
  onLocation: () => void;
  onTools: () => void;
}

export function AppShell({
  children,
  chromeMode,
  brandHref,
  brandImageSrc,
  status,
  tracking,
  journeyText = 'Journey',
  labels = { journey: 'Journey', location: 'Location', tools: 'Tools' },
  journeyLauncherRef,
  toolsLauncherRef,
  onJourney,
  onLocation,
  onTools
}: AppShellProps) {
  return (
    <main className="app-shell" data-ui-mode={chromeMode} data-testid="app-shell">
      <header className="app-chrome" aria-label="Application controls">
        <a className="brand-link" href={brandHref} aria-label="Beach Lab" title="Beach Lab">
          <img src={brandImageSrc} alt="" aria-hidden="true" />
        </a>
        <button ref={journeyLauncherRef} className="chrome-button journey-launcher" type="button" onClick={onJourney} aria-label={labels.journey} title={labels.journey}>
          <Route aria-hidden="true" />
          <span>{journeyText}</span>
        </button>
        <button
          className={`chrome-button location-launcher ${tracking !== 'off' ? 'active' : ''}`}
          type="button"
          onClick={onLocation}
          aria-label={labels.location}
          title={labels.location}
          aria-pressed={tracking !== 'off'}
        >
          {tracking === 'follow' ? <Navigation aria-hidden="true" /> : <LocateFixed aria-hidden="true" />}
        </button>
        <button ref={toolsLauncherRef} className="chrome-button tools-launcher" type="button" onClick={onTools} aria-label={labels.tools} title={labels.tools}>
          <SlidersHorizontal aria-hidden="true" />
          <span>{labels.tools}</span>
        </button>
        <span className="sr-status" role="status" aria-live="polite">{status}</span>
      </header>
      {children}
    </main>
  );
}
